import React, {ComponentType, useCallback, useEffect, useMemo, useState} from 'react';
import {
  Button as MUIButton,
  TableContainer,
  TableRow,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip, Table, Chip,
} from '@mui/material';
import {styled} from '@mui/material/styles';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import {AdjustRounded, DoneRounded} from '@mui/icons-material';

import api from '../../../services/api';
import toast from 'react-hot-toast';

import {formatNumber} from '../../../reducer/actions/formatNumber';
import {SPNumberField} from '../../Table/fields/SPNumberField';
import {MoreOptions} from '../../Table/assets/MoreOptions';
import {SlidingPaneEdit} from '../../Page/SlidingPaneEdit';
import {service} from '../../../schema/service';
import {useStateContext} from '../../../hooks/useStateContext';
import {hasPermissionByPriority} from '../../../core/permissions/hasPermission';
import {ROLE_PRIORITIES} from '../../../core/permissions/permissions';
import {hasPermissionId} from '../../../core/permissions/hasPermissionId';
import {ServicePackageCard} from "./ServicePackageCard";

type Props = {
  formState: any;
  setFormState: any;
  slidingRef: React.RefObject<any>;
};

const Item = styled(Paper)(({theme}) => ({
  backgroundColor: 'transparent',
  padding: theme.spacing(1),
  boxShadow: 'none',
}));


type ServicesResponse = {
  base: any[];
  extra: any[];
};


const SectionTitle = ({children, sx}: any) => (
  <Typography color="darkgrey" variant="body2" sx={{mb: '5px', ...sx}}>
    {children}
  </Typography>
);


export const ServicesSchema: ComponentType<Props> = ({formState, setFormState}) => {
  const [loading, setLoading] = useState(false);
  const [services, setServices] = useState<ServicesResponse>({base: [], extra: []});
  const [openViewPrices, setOpenViewPrices] = useState<any>(undefined);
  const [openedSlidingPane, setOpenedSlidingPane] = useState<any>({});
  const [openConfirmDialog, setOpenConfirmDialog] = useState<any>(false);
  const [selectedServiceGroupId, setSelectedServiceGroupId] = useState<number | null>(null);

  const {user} = useStateContext();
  const isAdmin = hasPermissionByPriority(user.roles, ROLE_PRIORITIES[1]);
  const isFinance = hasPermissionId(user.roles, 101);

  const openViewPriceColumns = useMemo(() => {
    const cols: any = {};
    for (let i = 1; i < 51; i++) cols[`size_${i}`] = i;
    return cols;
  }, []);

  const fetchServices = useCallback(() => {
    if (!formState?.activity?.id) return;

    setLoading(true);
    api
      .get(`/activity-reservations/quick-add/${formState.activity.id}`)
      .then((res: any) => {
        setServices(res);
      })
      .finally(() => setLoading(false));
  }, [formState?.activity?.id]);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  const handleSelectPackage = useCallback(
    (packageId: number) => {
      setSelectedServiceGroupId(packageId);
      setFormState((prev: any) => ({...prev, selected_service_group: packageId}));
    },
    [setFormState]
  );

  const handleDeleteServiceInPackage = useCallback(async (deletedRow: any) => {
    setOpenConfirmDialog(false);
    setLoading(true);

    api
      .delete(`/service-packages/service/${deletedRow?.id}`)
      .then(() => {
        fetchServices();
        toast.success(
          `Successfully deleted Service: ${deletedRow?.name ?? deletedRow?.id}`
        );
      })
      .catch((error: any) => {
        const msgs: string[] = [];
        for (const k in error) {
          for (const i in error[k]) msgs.push(String(error[k][i]));
        }
        toast.error(msgs.join(','));
      })
      .finally(() => setLoading(false));
  }, [fetchServices]);

  return (
    <>
      <SectionTitle>
        <Chip
          label={
          <span>
            Size: <b>{formState?.group_size}</b> person
          </span>
          }
        />
      </SectionTitle>
      <SectionTitle>Included Services</SectionTitle>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Item>
            {services.base.length ? services.base.map((item: any) => (
              <ServicePackageCard
                key={item.id}
                item={item}
                selectable={false}
                selectedServiceGroupId={selectedServiceGroupId}
                onSelect={handleSelectPackage}
                onEdit={(row: any) => setOpenedSlidingPane({schema: service, row, tab: 'services'})}
                onDelete={(row: any) => setOpenConfirmDialog(row)}
                isAdmin={isAdmin}
                isFinance={isFinance}
                setOpenViewPrices={setOpenViewPrices}
              />
            )) : <Box
              sx={{
                background: '#fff',
                p: '10px',
                borderRadius: '10px'
              }}
            ><Typography color={'darkgrey'}>No Included Service available</Typography></Box>}
          </Item>
        </Grid>
      </Grid>

      <SectionTitle sx={{mt: '15px'}}>Optional Services</SectionTitle>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Item>
            {services.extra.length ? services.extra.map((item: any) => (
              <ServicePackageCard
                key={item.id}
                item={item}
                selectable={true}
                selectedServiceGroupId={selectedServiceGroupId}
                onSelect={handleSelectPackage}
                onEdit={(row: any) => setOpenedSlidingPane({schema: service, row, tab: 'services'})}
                onDelete={(row: any) => setOpenConfirmDialog(row)}
                isAdmin={isAdmin}
                isFinance={isFinance}
                setOpenViewPrices={setOpenViewPrices}
              />
            )) : <Box
              sx={{
                background: '#fff',
                p: '10px',
                borderRadius: '10px'
              }}
            ><Typography color={'darkgrey'}>No Optional Service available</Typography></Box>}
          </Item>
        </Grid>
      </Grid>

      {/* Prices dialog */}
      {openViewPrices?.name && (
        <Dialog
          open={!!openViewPrices}
          onClose={() => setOpenViewPrices(undefined)}
          sx={{
            '& .MuiPaper-root.MuiDialog-paper': {width: '100%', maxWidth: '100%'},
          }}
        >
          <DialogTitle>{`${openViewPrices?.name} - Prices`}</DialogTitle>
          <DialogContent>
            <TableContainer component="div">
              <Table size="small" sx={{minWidth: 6000}}>
                <TableHead
                  sx={{
                    '& .MuiTableCell-root': {color: 'inherit', padding: '5px'},
                    '& .MuiTableRow-root .MuiTableCell-root.MuiTableCell-head': {border: 'none'},
                    background: '#256861',
                    color: '#fff',
                  }}
                >
                  <TableRow>
                    {Object.keys(openViewPriceColumns).map((column: any) => (
                      <TableCell key={column} sx={{width: 120, textAlign: 'center'}}>
                        {openViewPriceColumns[column]}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  <TableRow>
                    {Object.keys(openViewPriceColumns).map((column: any) => (
                      <TableCell key={column} sx={{borderRight: '1px solid #e3e3e3', padding: '5px'}}>
                        <SPNumberField
                          disabled
                          onChange={() => {
                          }}
                          defaultValue={openViewPrices?.[column] ?? null}
                          isActive
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </DialogContent>
        </Dialog>
      )}

      {/* Edit sliding pane */}
      {openedSlidingPane?.row?.id && (
        <SlidingPaneEdit
          demo={false}
          schema={openedSlidingPane?.schema}
          id={openedSlidingPane?.row?.id}
          onSubmit={() => {
            fetchServices();
            setOpenedSlidingPane({});
            return true;
          }}
          propertiesFromTable={openedSlidingPane?.row ?? {}}
          onClose={() => setOpenedSlidingPane({})}
        />
      )}

      {/* Confirm delete */}
      {openConfirmDialog && (
        <Dialog open={!!openConfirmDialog} onClose={() => setOpenConfirmDialog(false)}>
          <DialogTitle>{'Are you sure you want to delete it?'}</DialogTitle>
          <DialogActions>
            <MUIButton onClick={() => setOpenConfirmDialog(false)}>Cancel</MUIButton>
            <MUIButton
              variant="contained"
              color="error"
              onClick={() => handleDeleteServiceInPackage(openConfirmDialog)}
              autoFocus
            >
              Delete
            </MUIButton>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};
